<template>
  <div class="amprod-sidebar">
    <div class="amprod-sidebar-content">
      <h3>Selected Configuration</h3>

      <div class="amprod-selected-parts">
        <template v-for="(selections, slotId) in configuration" :key="slotId">
          <div
            v-for="option in Array.isArray(selections)
              ? selections
              : [selections]"
            :key="`${slotId}-${option.id}`"
            class="amprod-selected-part configured"
          >
            <div class="part-header">
              <div class="part-title">
                {{ option.description }}
              </div>
            </div>
            <div class="part-number">
              {{ option.part_number }}
            </div>
            <div class="part-slot">
              {{ getSlotName(slotId) }}
            </div>

            <!-- Quantity controls and remove button -->
            <div class="option-controls">
              <QuantityDisplay
                :quantity="option.quantity || 1"
                label="Quantity:"
                variant="controls"
                :allow-decrease="(option.quantity || 1) > 0"
                :min-quantity="0"
                @decrease="handleOptionQuantityDecrease(slotId, option)"
                @increase="handleOptionQuantityIncrease(slotId, option)"
              />
              <button
                class="remove-option-btn"
                title="Remove this option"
                @click="handleRemoveOption(slotId, option)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path
                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                  />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </div>
          </div>
        </template>

        <div v-if="getTotalSelectionCount === 0" class="amprod-selected-part">
          <div class="part-not-selected">No options selected</div>
        </div>
      </div>

      <div class="amprod-configuration-summary">
        <div class="configuration-progress">
          <span class="progress-text">
            {{ getTotalSelectionCount }} option(s) selected
            <span v-if="getTotalQuantity > getTotalSelectionCount">
              ({{ getTotalQuantity }} total items)
            </span>
          </span>
        </div>

        <div class="configuration-actions">
          <QuantityDisplay
            :quantity="configurationQuantity"
            label=""
            variant="controls"
            :allow-decrease="configurationQuantity > 1"
            :min-quantity="1"
            @decrease="configurationQuantity--"
            @increase="configurationQuantity++"
          />

          <button
            class="amprod-add-to-cart-btn"
            :disabled="!isConfigurationValid"
            @click="handleAddToQuoteRequest"
          >
            {{ isEditMode ? "Update Quote" : "Add to Quote" }}
          </button>
        </div>

        <!-- Success message below button -->
        <div v-if="showSuccessMessage" class="success-message">
          <p class="success-text">
            {{ successMessageText }}
          </p>
          <a v-if="quoteFormUrl" :href="quoteFormUrl" class="view-quote-btn">
            View Quote Request
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuantityDisplay from "./common/QuantityDisplay.vue";

export default {
  name: "ConfiguratorSidebar",
  components: {
    QuantityDisplay,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    configurableSlots: {
      type: Array,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
    isConfigurationValid: {
      type: Boolean,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    initialQuantity: {
      type: Number,
      default: 1,
    },
  },
  emits: ["add-to-quote", "option-quantity-change"],
  data() {
    return {
      configurationQuantity: this.initialQuantity,
      showSuccessMessage: false,
      successMessageText: "",
      quoteFormUrl: "",
    };
  },
  computed: {
    getTotalSelectionCount() {
      return Object.values(this.configuration).reduce((total, selections) => {
        if (Array.isArray(selections)) {
          return total + selections.length;
        }
        return total + (selections ? 1 : 0);
      }, 0);
    },
    getTotalQuantity() {
      return Object.values(this.configuration).reduce((total, selections) => {
        if (Array.isArray(selections)) {
          return (
            total +
            selections.reduce((subtotal, option) => {
              return subtotal + (option.quantity || 1);
            }, 0)
          );
        }
        return total + (selections ? selections.quantity || 1 : 0);
      }, 0);
    },
  },
  watch: {
    initialQuantity: {
      immediate: true,
      handler(newQuantity) {
        this.configurationQuantity = newQuantity;
      },
    },
  },
  mounted() {
    // Get quote form URL from global data
    this.quoteFormUrl = window.amprodCartData?.quoteFormUrl || "";
  },
  methods: {
    getSlotName(slotId) {
      const slot = this.configurableSlots.find((slot) => {
        const slotDataId = slot.type?.code || slot.id || "default";
        return slotDataId === slotId;
      });

      return slot?.name || slot?.type?.name || slotId;
    },

    handleOptionQuantityIncrease(slotId, option) {
      const newQuantity = (option.quantity || 1) + 1;
      this.$emit("option-quantity-change", slotId, option, newQuantity);
    },

    handleOptionQuantityDecrease(slotId, option) {
      const currentQuantity = option.quantity || 1;
      if (currentQuantity > 0) {
        const newQuantity = currentQuantity - 1;
        this.$emit("option-quantity-change", slotId, option, newQuantity);
      }
    },

    handleRemoveOption(slotId, option) {
      // Set quantity to 0, which will trigger deselection
      console.log("Remove option clicked:", slotId, option);
      this.$emit("option-quantity-change", slotId, option, 0);
    },

    handleAddToQuoteRequest() {
      if (!this.isConfigurationValid) {
        alert(
          "Please ensure all required options are selected before adding to your quote request."
        );
        return;
      }

      // Prepare the configuration data
      const quoteData = {
        product: this.product,
        options: [],
        quantity: this.configurationQuantity, // Use the selected quantity
      };

      // Add selected options to the quote data (no slot information)
      for (const selections of Object.values(this.configuration)) {
        const optionsArray = Array.isArray(selections)
          ? selections
          : [selections];

        for (const option of optionsArray) {
          quoteData.options.push({
            id: option.id,
            part_number: option.part_number,
            description: option.description,
            quantity: option.quantity || 1,
            // Include any other relevant option properties but exclude slot info
            price: option.price,
            weight: option.weight,
            // Add other option properties as needed
          });
        }
      }

      // Emit the add-to-quote event
      this.$emit("add-to-quote", quoteData);

      // Show success message below the button (stays visible)
      this.successMessageText = this.isEditMode
        ? "Configuration updated successfully!"
        : "Product added to quote request successfully!";
      this.showSuccessMessage = true;
    },
  },
};
</script>

<style scoped>
.amprod-sidebar {
  width: 350px;
  flex-shrink: 0;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

.amprod-sidebar-content {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.amprod-sidebar h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #333;
  border-bottom: 2px solid #0073aa;
  padding-bottom: 10px;
  flex-shrink: 0;
}

.amprod-selected-parts {
  margin-bottom: 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.amprod-selected-part {
  margin-bottom: 15px;
  padding: 15px;
  border-radius: 6px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.amprod-selected-part.configured {
  border-color: #4caf50;
  background-color: #f8fff8;
}

.part-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.part-title {
  font-weight: 600;
  color: #0073aa;
  font-size: 14px;
}

.part-number {
  color: #666;
  font-size: 13px;
  margin-top: 4px;
}

.part-slot {
  color: #999;
  font-size: 12px;
  margin-top: 2px;
  font-style: italic;
}

.option-controls {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.remove-option-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.remove-option-btn:hover {
  background-color: #c82333;
  transform: scale(1.05);
}

.remove-option-btn svg {
  display: block;
}

.part-not-selected {
  color: #999;
  font-style: italic;
  font-size: 14px;
}

.amprod-configuration-summary {
  border-top: 1px solid #e0e0e0;
  padding-top: 20px;
  flex-shrink: 0;
  background-color: #f8f9fa;
}

.configuration-progress {
  margin-bottom: 20px;
}

.progress-text {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.configuration-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.amprod-add-to-cart-btn {
  flex: 1;
  background-color: #0073aa;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.amprod-add-to-cart-btn:hover:not(:disabled) {
  background-color: #005a87;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 115, 170, 0.3);
}

.amprod-add-to-cart-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.success-message {
  margin-top: 15px;
  padding: 15px;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  text-align: center;
}

.success-text {
  color: #155724;
  font-weight: 600;
  margin: 0 0 10px 0;
  font-size: 14px;
}

.view-quote-btn {
  display: inline-block;
  background-color: #0073aa;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.view-quote-btn:hover {
  background-color: #005a87;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 115, 170, 0.3);
}

/* Responsive design */
@media (max-width: 1024px) {
  .amprod-sidebar {
    width: 100%;
    position: static;
    max-height: none;
    order: -1; /* Show sidebar at top on mobile */
  }

  .amprod-sidebar-content {
    max-height: 400px;
    overflow-y: auto;
  }
}

@media (max-width: 768px) {
  .amprod-sidebar-content {
    padding: 15px;
  }

  .configuration-actions {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .amprod-add-to-cart-btn {
    width: 100%;
  }
}
</style>
